import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { filter, firstValueFrom, take, timeout } from 'rxjs';
import { AppState } from 'src/app/app.reducer';
import { operaComoAprobado } from 'src/app/shared/utils/estado-compliance.util';

import {
  AsistenteClienteService,
  FondeoAsistente,
  MontoAsistente,
  OperacionAsistente,
  TasaAsistente,
} from '../../services/asistente-cliente.service';
import { ReporteClienteService } from '../../services/reporte-cliente.service';
import { TasaHome, TramoUsdtHome } from '../home-rates/models/home-rates.model';
import * as RH from '../home-rates/rules/home-rates.rules';
import { HomeRatesService } from '../home-rates/services/home-rates.service';
import * as RR from '../remittance-hub/rules/remittance-rules';
import {
  ClienteMensaje,
  MotivoOperador,
  clienteDeUsuario,
  formatearMonto,
  mensajeFondeo,
  mensajeFondeoFueraDeLista,
  mensajeGeneral,
  mensajeMotivo,
  mensajeNoVesFondeo,
  mensajeOperacion,
  mensajeOperacionFueraDeLista,
} from './asistente-operador.rules';
import { TasasAsistente, cicloListo, tasasParaAsistente, tramosUsdtDe, tramosUsdtVentaDe } from './asistente-tasas.rules';

type Entrada =
  | { tipo: 'bot'; texto: string }
  | { tipo: 'cliente'; texto: string }
  | { tipo: 'saldo'; saldos: MontoAsistente[]; consultadoEn: string | null }
  | { tipo: 'operaciones'; titulo: string; lista: OperacionAsistente[]; paraOperador?: boolean }
  | { tipo: 'detalle'; op: OperacionAsistente; pasos: Paso[] }
  | { tipo: 'fondeos'; titulo: string; lista: FondeoAsistente[]; paraOperador?: boolean }
  | { tipo: 'ultimoFondeo'; fondeo: FondeoAsistente; pasos: Paso[] }
  | { tipo: 'noVes'; ultimo: FondeoAsistente | null }
  | { tipo: 'tasa'; tasa: TasaAsistente; consultadoEn: string | null }
  | { tipo: 'tasas'; tasas: TasasAsistente; consultadoEn: string | null }
  | { tipo: 'operador' }
  | { tipo: 'error'; texto: string };

export interface Paso {
  estado: 'hecho' | 'ahora' | 'pendiente' | 'devuelto';
  titulo: string;
  detalle: string | null;
}

interface Opcion {
  texto: string;
  accion: () => void;
  operador?: boolean;
  /** Con ícono se pinta como botón grande, igual que los del menú. */
  icono?: string;
}

interface Boton {
  texto: string;
  icono: string;
  accion: () => void;
  ancho?: boolean;
}

/** Espera mínima del "escribiendo", para que la respuesta no aparezca de golpe. */
const PAUSA_MS = 350;

/**
 * Cuánto se espera el ciclo de la tarjeta de tasas del home. Pasado este tiempo
 * se cae a la consulta `TASA` del API, que sólo trae el riel propio.
 */
const ESPERA_TASAS_MS = 8000;

/**
 * Panel del asistente de botones (fase 1, sin IA ni texto libre).
 *
 * Todo lo que muestra sale de `asistente-cliente` en el API, que sólo lee de
 * nuestra base, salvo "Tasa de hoy": esa toma las filas de la tarjeta "Tasas
 * del día" del home (`HomeRatesService`), para que el cliente vea los mismos
 * corredores y el mismo número en los dos lugares. El texto se pinta siempre
 * por interpolación de Angular, nunca con `innerHTML`.
 *
 * "Hablar con un operador" pregunta primero el motivo (una operación, un
 * fondeo, la cuenta u otro tema) para que el mensaje llegue con el contexto, y
 * siempre con el nombre y el ID del cliente. Lo que abre WhatsApp es siempre el
 * toque final del cliente, sin esperar nada antes, para que el navegador del
 * teléfono no bloquee la ventana: las listas se cargan antes de ese toque.
 */
@Component({
  selector: 'app-asistente-cliente',
  templateUrl: './asistente-cliente.component.html',
  styleUrls: ['./asistente-cliente.component.sass'],
})
export class AsistenteClienteComponent implements AfterViewInit, OnDestroy {
  @Output() cerrar = new EventEmitter<void>();
  @ViewChild('registro') registro?: ElementRef<HTMLElement>;
  @ViewChild('panel') panel?: ElementRef<HTMLElement>;

  entradas: Entrada[] = [];
  opciones: Opcion[] = [];
  mostrarMenu = true;
  escribiendo = false;
  ocupado = false;

  /** Las opciones con ícono van como botones grandes; las demás, como chips. */
  get conIcono(): Opcion[] {
    return this.opciones.filter((o) => !!o.icono);
  }

  get sinIcono(): Opcion[] {
    return this.opciones.filter((o) => !o.icono);
  }

  readonly grupos: { titulo: string; botones: Boton[] }[] = [
    {
      titulo: 'Mi dinero',
      botones: [
        { texto: 'Mi saldo', icono: 'account_balance_wallet', accion: () => this.saldo(), ancho: true },
      ],
    },
    {
      titulo: 'Operaciones',
      botones: [
        { texto: 'Mi última operación', icono: 'schedule', accion: () => this.ultimaOperacion() },
        { texto: 'Estado de mis operaciones', icono: 'list_alt', accion: () => this.elegirCantidad() },
      ],
    },
    {
      titulo: 'Fondeos',
      botones: [
        { texto: 'Mis últimos 5 fondeos', icono: 'south', accion: () => this.fondeos() },
        { texto: 'Estado de mi último fondeo', icono: 'task_alt', accion: () => this.ultimoFondeo() },
        { texto: 'Datos para fondear', icono: 'account_balance', accion: () => this.datosFondeo() },
        { texto: '¿No ves tu fondeo?', icono: 'help_outline', accion: () => this.noVesFondeo() },
      ],
    },
    {
      titulo: 'Ayuda',
      botones: [
        { texto: 'Tasa de hoy', icono: 'trending_up', accion: () => this.tasa() },
        { texto: 'Descargar mi historial', icono: 'download', accion: () => this.historial() },
      ],
    },
  ];

  private vivo = true;
  /** Si el asistente está colgado del ciclo de tasas del home. */
  private enTasasHome = false;
  private readonly movimientoReducido =
    typeof window !== 'undefined' &&
    !!window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  constructor(
    private readonly asistente: AsistenteClienteService,
    private readonly reporteCliente: ReporteClienteService,
    private readonly router: Router,
    private readonly cdr: ChangeDetectorRef,
    private readonly tasasHome: HomeRatesService,
    private readonly store: Store<AppState>,
  ) {
    const nombre = this.asistente.nombre;
    this.entradas.push({
      tipo: 'bot',
      texto: `Hola${nombre ? ', ' + nombre : ''}. Soy el asistente de Aguacate. Elige una opción.`,
    });
  }

  ngAfterViewInit(): void {
    setTimeout(
      () => this.panel?.nativeElement.querySelector<HTMLElement>('button.tile')?.focus(),
      0,
    );
  }

  ngOnDestroy(): void {
    this.vivo = false;
    this.soltarTasasHome();
  }

  @HostListener('document:keydown.escape')
  alEscape(): void {
    this.cerrar.emit();
  }

  // ---------------------------------------------------------------------------
  // Acciones de la interfaz
  // ---------------------------------------------------------------------------

  tocarBoton(b: Boton): void {
    if (this.ocupado) return;
    this.cliente(b.texto);
    b.accion();
  }

  tocarOpcion(o: Opcion): void {
    if (this.ocupado && !o.operador) return;
    this.cliente(o.texto);
    o.accion();
  }

  volverAlMenu(): void {
    if (this.ocupado) return;
    this.cliente('Menú');
    this.bot('¿Qué quieres consultar?');
    this.menu();
  }

  /**
   * Botón "Hablar con un operador" de la barra: pregunta el motivo. Si hay una
   * consulta en curso no se la pisa: abre WhatsApp con el mensaje general.
   */
  hablarConOperador(): void {
    if (this.ocupado) {
      this.abrirOperador(mensajeGeneral(this.quien()));
      return;
    }
    this.cliente('Hablar con un operador');
    this.bot('¿Sobre qué quieres hablar? Así el operador ya sabe de qué se trata y te atiende más rápido.');
    const motivo = (m: MotivoOperador) => () => this.abrirOperador(mensajeMotivo(m, this.quien()));
    this.chips([
      { texto: 'Una operación', icono: 'north_east', accion: () => this.operacionesParaOperador() },
      { texto: 'Un fondeo', icono: 'south_west', accion: () => this.fondeosParaOperador() },
      { texto: 'Mi cuenta', icono: 'person', operador: true, accion: motivo('CUENTA') },
      { texto: 'Otro tema', icono: 'help_outline', operador: true, accion: motivo('OTRO') },
      {
        texto: 'Hablar directo, sin elegir',
        operador: true,
        accion: () => this.abrirOperador(mensajeGeneral(this.quien())),
      },
    ]);
    this.bajar();
  }

  /** Directo desde el toque: nada de `await` antes de abrir WhatsApp. */
  private abrirOperador(texto: string): void {
    this.asistente.abrirWhatsApp(texto);
    this.entradas.push({ tipo: 'operador' });
    this.opciones = [];
    this.mostrarMenu = false;
    this.bajar();
  }

  /** En la lista para elegir operador, tocar la operación abre WhatsApp con ella. */
  tocarOperacion(op: OperacionAsistente, paraOperador = false): void {
    if (paraOperador) {
      this.cliente(`Operación N.º ${op.numero}`);
      this.abrirOperador(mensajeOperacion(op, this.quien()));
      return;
    }
    if (this.ocupado) return;
    this.cliente(`Ver operación N.º ${op.numero}`);
    this.entradas.push({ tipo: 'detalle', op, pasos: pasosOperacion(op) });
    this.chips([
      {
        texto: 'Consultar esta operación con un operador',
        operador: true,
        accion: () => this.abrirOperador(mensajeOperacion(op, this.quien())),
      },
    ]);
    this.bajar();
  }

  /** Sólo en la lista para elegir operador: los fondeos no tienen detalle propio. */
  tocarFondeo(f: FondeoAsistente): void {
    this.cliente(`Fondeo ${f.referencia}`);
    this.abrirOperador(mensajeFondeo(f, this.quien()));
  }

  // ---------------------------------------------------------------------------
  // Consultas. Cada una trae los datos y devuelve cómo pintarlos.
  // ---------------------------------------------------------------------------

  private saldo(): void {
    void this.pedir(async () => {
      const r = await this.asistente.consultar('SALDO');
      return () => {
        this.entradas.push({
          tipo: 'saldo',
          saldos: r?.saldos ?? [],
          consultadoEn: r?.consultadoEn ?? null,
        });
        this.chips([]);
      };
    });
  }

  private ultimaOperacion(): void {
    void this.pedir(async () => {
      const r = await this.asistente.consultar('ULTIMA_OPERACION');
      return () => {
        const lista = r?.operaciones ?? [];
        if (!lista.length) {
          this.bot('Todavía no tienes operaciones.');
        } else {
          this.entradas.push({ tipo: 'operaciones', titulo: 'Tu última operación:', lista });
        }
        this.chips([]);
      };
    });
  }

  private elegirCantidad(): void {
    this.bot('¿Cuántas operaciones quieres ver?');
    this.chips([
      { texto: 'Últimas 5', accion: () => this.operaciones(5) },
      { texto: 'Últimas 10', accion: () => this.operaciones(10) },
    ]);
  }

  private operaciones(cantidad: 5 | 10): void {
    void this.pedir(async () => {
      const r = await this.asistente.consultar('OPERACIONES', cantidad);
      return () => {
        const lista = r?.operaciones ?? [];
        if (!lista.length) {
          this.bot('Todavía no tienes operaciones.');
        } else {
          this.entradas.push({
            tipo: 'operaciones',
            titulo:
              lista.length < cantidad
                ? `Tus operaciones (${lista.length}):`
                : `Tus últimas ${cantidad} operaciones:`,
            lista,
          });
        }
        this.chips([]);
      };
    });
  }

  private fondeos(): void {
    void this.pedir(async () => {
      const r = await this.asistente.consultar('FONDEOS');
      return () => {
        const lista = r?.fondeos ?? [];
        if (!lista.length) {
          this.bot('Todavía no tienes fondeos registrados.');
          this.chips([{ texto: 'Ver datos para fondear', accion: () => this.datosFondeo() }]);
          return;
        }
        this.entradas.push({
          tipo: 'fondeos',
          titulo: lista.length < 5 ? `Tus fondeos (${lista.length}):` : 'Tus últimos 5 fondeos:',
          lista,
        });
        this.chips([]);
      };
    });
  }

  private ultimoFondeo(): void {
    void this.pedir(async () => {
      const r = await this.asistente.consultar('ULTIMO_FONDEO');
      return () => {
        const fondeo = r?.fondeos?.[0];
        if (!fondeo) {
          this.bot('Todavía no tienes fondeos registrados.');
          this.chips([{ texto: 'Ver datos para fondear', accion: () => this.datosFondeo() }]);
          return;
        }
        this.entradas.push({ tipo: 'ultimoFondeo', fondeo, pasos: pasosFondeo(fondeo) });
        this.chips(
          fondeo.estado === 'ACREDITADO'
            ? []
            : [
                {
                  texto: 'Consultar este fondeo con un operador',
                  operador: true,
                  accion: () => this.abrirOperador(mensajeFondeo(fondeo, this.quien())),
                },
              ],
        );
      };
    });
  }

  private noVesFondeo(): void {
    void this.pedir(async () => {
      const r = await this.asistente.consultar('ULTIMO_FONDEO');
      return () => {
        const ultimo = r?.fondeos?.[0] ?? null;
        this.entradas.push({ tipo: 'noVes', ultimo });
        this.chips([
          { texto: 'Ver datos para fondear', accion: () => this.datosFondeo() },
          {
            texto: 'Sigo sin verlo: hablar con un operador',
            operador: true,
            accion: () => this.abrirOperador(mensajeNoVesFondeo(ultimo, this.quien())),
          },
        ]);
      };
    });
  }

  /**
   * "Hablar con un operador" → "Una operación": las últimas 5 para elegir.
   * Tocar una abre WhatsApp con sus datos (ver `tocarOperacion`).
   */
  private operacionesParaOperador(): void {
    const sinElegir = this.opcionSinElegir();
    void this.pedir(async () => {
      const r = await this.asistente.consultar('OPERACIONES', 5);
      return () => {
        const lista = r?.operaciones ?? [];
        if (!lista.length) {
          this.bot('Todavía no tienes operaciones. Cuéntale al operador qué necesitas.');
        } else {
          this.entradas.push({
            tipo: 'operaciones',
            titulo: '¿Cuál operación? Toca la que quieres consultar.',
            lista,
            paraOperador: true,
          });
        }
        this.chips([
          {
            texto: 'No está en la lista',
            operador: true,
            accion: () => this.abrirOperador(mensajeOperacionFueraDeLista(this.quien())),
          },
          sinElegir,
        ]);
      };
    }, [sinElegir]);
  }

  /** Igual que `operacionesParaOperador`, con los últimos 5 fondeos. */
  private fondeosParaOperador(): void {
    const sinElegir = this.opcionSinElegir();
    void this.pedir(async () => {
      const r = await this.asistente.consultar('FONDEOS');
      return () => {
        const lista = r?.fondeos ?? [];
        if (!lista.length) {
          this.bot('Todavía no tienes fondeos registrados. Cuéntale al operador qué necesitas.');
        } else {
          this.entradas.push({
            tipo: 'fondeos',
            titulo: '¿Cuál fondeo? Toca el que quieres consultar.',
            lista,
            paraOperador: true,
          });
        }
        this.chips([
          {
            texto: 'No está en la lista',
            operador: true,
            accion: () => this.abrirOperador(mensajeFondeoFueraDeLista(this.quien())),
          },
          sinElegir,
        ]);
      };
    }, [sinElegir]);
  }

  private opcionSinElegir(): Opcion {
    return {
      texto: 'Hablar sin elegir',
      operador: true,
      accion: () => this.abrirOperador(mensajeGeneral(this.quien())),
    };
  }

  /**
   * Lleva a Recargar saldo, la pantalla que ya muestra los datos vigentes para
   * transferir. No se repiten acá: una sola fuente, sin riesgo de mostrar una
   * cuenta distinta a la de esa pantalla.
   */
  private datosFondeo(): void {
    this.cerrar.emit();
    void this.router.navigate(['/wallet/client/rechargeAccount']);
  }

  private tasa(): void {
    void this.pedir(async () => {
      const home = await this.tasasDelHome();
      if (home) {
        return () => {
          this.entradas.push({ tipo: 'tasas', tasas: home.tasas, consultadoEn: home.consultadoEn });
          this.chips([]);
        };
      }

      // Respaldo: la consulta del API, sólo el riel propio.
      const r = await this.asistente.consultar('TASA');
      return () => {
        if (!r?.tasa?.disponible) {
          this.bot(
            'La tasa se está actualizando en este momento. La verás confirmada al crear tu operación.',
          );
        } else {
          this.entradas.push({ tipo: 'tasa', tasa: r.tasa, consultadoEn: r.consultadoEn ?? null });
        }
        this.chips([]);
      };
    });
  }

  /**
   * Toma una foto de la tarjeta de tasas del home. Si el home ya está en
   * pantalla, sus filas están frescas y no se pide nada; si no, se arranca un
   * ciclo y se espera a que cierre.
   *
   * El perfil no se decide acá cuando el home ya lo fijó: dos criterios
   * distintos se borrarían los valores el uno al otro. Cuando nadie lo fijó
   * todavía, se usa el del home con el dato que hay a mano: sin aprobación de
   * compliance, sólo las tasas públicas (el home, además, habilita las demás a
   * un PRE operativo; acá se queda del lado seguro).
   */
  private async tasasDelHome(): Promise<{ tasas: TasasAsistente; consultadoEn: string | null } | null> {
    try {
      const auth: any = await firstValueFrom(this.store.select('auth').pipe(take(1)));
      const user = auth?.user;
      if (!user) return null;

      // Lo que dejó fijado el home se respeta, salvo que sea de otra cuenta o
      // de antes de la aprobación: eso ya no describe a este cliente.
      const perfilCuenta = RR.perfilDeTipoId(user.tipoId);
      // Aprobado (3) o bloqueado (4): ve las tasas de un aprobado.
      const aprobado = operaComoAprobado(user);
      const conf = this.tasasHome.configuracion;
      if (!conf || conf.perfil !== perfilCuenta || (aprobado && conf.soloPublicos)) {
        this.tasasHome.setPerfil(perfilCuenta, !aprobado);
      }
      // Después del perfil: registrarse antes arrancaba un ciclo con el viejo.
      this.enTasasHome = true;
      this.tasasHome.registrar();
      const perfil = this.tasasHome.configuracion?.perfil;
      if (!perfil) return null;

      const st = await firstValueFrom(
        this.tasasHome.estado$.pipe(
          filter((s) => cicloListo(s, perfil)),
          take(1),
          timeout(ESPERA_TASAS_MS),
        ),
      );
      const tasas = tasasParaAsistente(st);
      return tasas
        ? {
            tasas,
            consultadoEn: st.ultimoCicloEn != null ? new Date(st.ultimoCicloEn).toISOString() : null,
          }
        : null;
    } catch {
      return null;
    } finally {
      this.soltarTasasHome();
    }
  }

  /** Una sola vez por `registrar`: soltar de más le apagaría el ciclo al home. */
  private soltarTasasHome(): void {
    if (!this.enTasasHome) return;
    this.enTasasHome = false;
    this.tasasHome.soltar();
  }

  private historial(): void {
    if (this.reporteCliente.descargando) {
      this.bot('Tu historial ya se está descargando.');
    } else {
      this.bot('Estamos preparando tu historial en Excel. Te avisamos cuando empiece la descarga.');
      void this.reporteCliente.descargar();
    }
    this.chips([]);
  }

  // ---------------------------------------------------------------------------
  // Plomería
  // ---------------------------------------------------------------------------

  /**
   * Corre una consulta con el indicador de "escribiendo". Lo que devuelve
   * `obtener` se pinta recién cuando termina la pausa, así la respuesta no
   * aparece debajo del indicador. Si falla, queda el mensaje en el panel con
   * "Intentar de nuevo" y las opciones de `siFalla`; "Hablar con un operador"
   * sigue en la barra de abajo.
   */
  private async pedir(
    obtener: () => Promise<() => void>,
    siFalla: Opcion[] = [],
  ): Promise<void> {
    this.ocupado = true;
    this.escribiendo = true;
    this.opciones = [];
    this.mostrarMenu = false;
    this.bajar();

    const inicio = Date.now();
    let pintar: (() => void) | null = null;
    let error: unknown = null;
    try {
      pintar = await obtener();
    } catch (e) {
      error = e;
    }
    await this.esperarPausa(inicio);
    if (!this.vivo) return;

    this.escribiendo = false;
    this.ocupado = false;
    if (pintar) {
      try {
        pintar();
      } catch (e) {
        error = e;
        pintar = null;
      }
    }
    if (!pintar) {
      this.entradas.push({ tipo: 'error', texto: this.asistente.mensajeDeError(error) });
      this.chips([
        { texto: 'Intentar de nuevo', accion: () => void this.pedir(obtener, siFalla) },
        ...siFalla,
      ]);
    }
    this.cdr.markForCheck();
    this.bajar();
  }

  private esperarPausa(inicio: number): Promise<void> {
    const resto = this.movimientoReducido ? 0 : PAUSA_MS - (Date.now() - inicio);
    return resto > 0 ? new Promise((r) => setTimeout(r, resto)) : Promise.resolve();
  }

  private menu(): void {
    this.opciones = [];
    this.mostrarMenu = true;
    this.bajar();
  }

  private chips(opciones: Opcion[]): void {
    this.opciones = opciones;
    this.mostrarMenu = false;
  }

  private bot(texto: string): void {
    this.entradas.push({ tipo: 'bot', texto });
    this.bajar();
  }

  private cliente(texto: string): void {
    this.entradas.push({ tipo: 'cliente', texto });
    this.bajar();
  }

  /**
   * Quién escribe, leído del store en el momento. Sin `await`: el store
   * entrega el valor actual en el acto, así abrir WhatsApp sigue pegado al toque.
   */
  private quien(): ClienteMensaje {
    let user: any = null;
    this.store
      .select('auth')
      .pipe(take(1))
      .subscribe((auth: any) => (user = auth?.user ?? null))
      .unsubscribe();
    return clienteDeUsuario(user);
  }

  private bajar(): void {
    setTimeout(() => {
      const el = this.registro?.nativeElement;
      if (el) el.scrollTop = el.scrollHeight;
    }, 0);
  }

  // ---------------------------------------------------------------------------
  // Formato (lo usa la plantilla)
  // ---------------------------------------------------------------------------

  monto(m: MontoAsistente | null): string {
    return formatearMonto(m);
  }

  fecha(iso: string | null): string {
    return formatearFecha(iso);
  }

  /** "1 CLP = 0,0451 VES" o "1 VES = 22,17 CLP", según cómo la publica el API. */
  textoTasa(t: TasaAsistente, tasa: number): string {
    const n = new Intl.NumberFormat('es-CL', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 4,
    }).format(tasa);
    return t.unidad === 'CLP_POR_VES' ? `1 VES = ${n} CLP` : `1 CLP = ${n} VES`;
  }

  /** "CLP 15.000 a 99.999". */
  textoTramo(desde: number, hasta: number): string {
    const f = new Intl.NumberFormat('es-CL', { maximumFractionDigits: 0 });
    return `CLP ${f.format(desde)} a ${f.format(hasta)}`;
  }

  /** "Bs. por 1 CLP, según cuánto envíes", igual que la tarjeta del home. */
  unidadTasa(t: TasaHome): string {
    return RH.etiquetaUnidad(t.def);
  }

  esParTasa(t: TasaHome): boolean {
    return RH.esPar(t.def);
  }

  /** Los pisos del cliente VE salen de una conversión: se avisa. */
  tramosAproximados(t: TasaHome): boolean {
    return RH.pisosAproximados(t.def);
  }

  /** Tasa de un tramo USDT con formato es-CL, como su rótulo (y el home). */
  tasaTramoUsdt(tr: TramoUsdtHome, t: TasaHome): string {
    return RH.formatearTasa(tr.tasa, t.def.decimales);
  }

  /** Tramos VES→USDT de la fila USDT (cliente VE). `null` = como siempre. */
  tramosUsdtTasa(t: TasaHome): TramoUsdtHome[] | null {
    return tramosUsdtDe(t);
  }

  /** Tramos USDT→VES de la fila USDT (cliente VE). `null` = como siempre. */
  tramosUsdtVentaTasa(t: TasaHome): TramoUsdtHome[] | null {
    return tramosUsdtVentaDe(t);
  }

  trackTasa(_: number, t: TasaHome): string {
    return t.def.id;
  }

  claseEstado(estado: string): string {
    switch (estado) {
      case 'COMPLETADA':
      case 'ACREDITADO':
        return 'st-ok';
      case 'RECHAZADA':
      case 'RECHAZADO':
      case 'CANCELADA':
      case 'ANULADA':
        return 'st-mal';
      case 'EN_REVISION':
      case 'ESPERANDO_PAGO':
        return 'st-rev';
      default:
        return 'st-proc';
    }
  }

  trackIndice(i: number): number {
    return i;
  }
}

// -----------------------------------------------------------------------------
// Funciones puras
// -----------------------------------------------------------------------------

export function formatearFecha(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('es-CL', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function pasosOperacion(op: OperacionAsistente): Paso[] {
  const creada: Paso = {
    estado: 'hecho',
    titulo: 'Operación creada',
    detalle: formatearFecha(op.fecha) || null,
  };
  switch (op.estado) {
    case 'COMPLETADA':
      return [
        creada,
        { estado: 'hecho', titulo: 'Procesada', detalle: null },
        { estado: 'hecho', titulo: 'Completada', detalle: formatearFecha(op.fechaTermino) || null },
      ];
    case 'RECHAZADA':
      return [
        creada,
        {
          estado: 'devuelto',
          titulo: 'Rechazada',
          detalle: 'Si necesitas el detalle, consúltalo con un operador.',
        },
      ];
    case 'CANCELADA':
    case 'ANULADA':
      return [
        creada,
        {
          estado: 'devuelto',
          titulo: op.estado === 'ANULADA' ? 'Anulada' : 'Cancelada',
          detalle: formatearFecha(op.fechaTermino) || null,
        },
      ];
    case 'ESPERANDO_PAGO':
      return [
        creada,
        {
          estado: 'ahora',
          titulo: 'Esperando tu pago',
          detalle: 'La operación avanza cuando recibamos tu pago.',
        },
        { estado: 'pendiente', titulo: 'Completada', detalle: null },
      ];
    default:
      return [
        creada,
        { estado: 'ahora', titulo: 'En proceso', detalle: 'Normalmente se completa en unos minutos.' },
        { estado: 'pendiente', titulo: 'Completada', detalle: null },
      ];
  }
}

export function pasosFondeo(f: FondeoAsistente): Paso[] {
  const recibido: Paso = {
    estado: 'hecho',
    titulo: 'Fondeo recibido',
    detalle: formatearFecha(f.fecha) || null,
  };
  switch (f.estado) {
    case 'ACREDITADO':
      return [recibido, { estado: 'hecho', titulo: 'Acreditado en tu saldo', detalle: null }];
    case 'RECHAZADO':
      return [
        recibido,
        { estado: 'devuelto', titulo: 'Rechazado', detalle: 'Consúltalo con un operador.' },
      ];
    case 'EN_REVISION':
      return [
        recibido,
        { estado: 'ahora', titulo: 'En revisión', detalle: 'No necesitas volver a transferir.' },
        { estado: 'pendiente', titulo: 'Acreditado en tu saldo', detalle: null },
      ];
    default:
      return [
        recibido,
        { estado: 'ahora', titulo: 'En proceso', detalle: 'No necesitas volver a transferir.' },
        { estado: 'pendiente', titulo: 'Acreditado en tu saldo', detalle: null },
      ];
  }
}
